/**
 * PayerStatsSidebar
 *
 * The patients-list quick filters: payers, coverage, and missing requirements,
 * each a toggle into the same filter state the list's filter bar edits
 * (ADR-012). The desktop rail also hosts cards other features contribute
 * (eligibility checks). On phones the page opens the same panel from a Quick
 * filters menu.
 */

import React from 'react';
import { EligibilityChecksSummaryCard } from '@features/eligibility-checks/components/EligibilityChecksSummaryCard';
import { useGetPayerStatsQuery } from '@features/patient-coverages';
import { RequirementsRailCard } from '@features/patient-requirements';
import { useSubjectLanguageVariants } from '@features/user';
import {
  ErrorOutlined as ErrorIcon,
  HealthAndSafetyOutlined as InsuranceIcon,
  PaymentsOutlined as PaymentsIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { Avatar, Box, Chip, Divider, Skeleton, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { CoverageBucket, ListCategory } from '../utils/patientListQuery';

/** The definition a rail row hands over when it is clicked. */
export interface RequirementFilterTarget {
  id: string;
  label: string;
  kind: string;
  /** The count shown on the row at the moment of the click. */
  count?: number;
}

export interface QuickFiltersProps {
  selected: Record<ListCategory, string[]>;
  onToggle: (category: ListCategory, value: string) => void;
  onRequirementSelect: (target: RequirementFilterTarget) => void;
  onRequirementDeselect: (definition: { id: string; kind: string }) => void;
  /** Rendered inside the phone menu, so the requirement counts load there too. */
  inMenu?: boolean;
}

/**
 * The panel itself. Counts come from `payer-stats` and cover every patient
 * the user can see (GDN-4118), not just the current results, so they are
 * labelled apart from the result count and never added together: coverage
 * categories overlap.
 */
export const QuickFilters: React.FC<QuickFiltersProps> = ({
  selected,
  onToggle,
  onRequirementSelect,
  onRequirementDeselect,
  inMenu = false,
}) => {
  const theme = useTheme();
  const { singular, plural } = useSubjectLanguageVariants();
  const { data, isLoading } = useGetPayerStatsQuery();

  const isSelected = (category: ListCategory, value: string) => selected[category].includes(value);

  const allBuckets: {
    bucket: CoverageBucket;
    label: string;
    count: number;
    color: string;
    icon: React.ReactNode;
  }[] = data
    ? [
        {
          bucket: 'insurance',
          label: 'Insurance',
          count: data.insurance_count,
          color: theme.palette.success.main,
          icon: <InsuranceIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />,
        },
        {
          bucket: 'self_pay',
          label: 'Self-pay',
          count: data.self_pay_count,
          color: theme.palette.info.main,
          icon: <PaymentsIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />,
        },
        {
          bucket: 'needs_review',
          label: 'Needs review',
          count: data.needs_review_count,
          color: theme.palette.warning.main,
          icon: <WarningIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />,
        },
        {
          bucket: 'no_coverage',
          label: 'No coverage',
          count: data.no_coverage_count,
          color: theme.palette.error.main,
          icon: <ErrorIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />,
        },
      ]
    : [];
  const buckets = allBuckets.filter(row => row.count > 0 || isSelected('coverage', row.bucket));

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.grey[100]}` }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: theme.palette.text.primary }}
          >
            Quick filters
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, display: 'block' }}
          >
            {/* No total here: payer-stats' total_patients sums per-payer counts
                and skips needs-review patients, so it is not a patient count. */}
            {`Counts cover every ${singular} you can see, not just this list. Select again to remove.`}
          </Typography>
        </Box>

        {/* Payer list */}
        <Typography
          variant="overline"
          sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary' }}
        >
          Payers
        </Typography>
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {isLoading ? (
            <Box sx={{ p: 1.5 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 0.5 }}
                >
                  <Skeleton variant="circular" width={28} height={28} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="70%" height={16} />
                  </Box>
                  <Skeleton width={24} height={16} />
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ py: 0.5 }}>
              {data?.payers.map(payer => {
                const active = isSelected('payers', payer.payer_uuid);
                return (
                  <Tooltip key={payer.payer_uuid} title={payer.payer_name} placement="left" arrow>
                    <Box
                      onClick={() => onToggle('payers', payer.payer_uuid)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onToggle('payers', payer.payer_uuid);
                        }
                      }}
                      aria-pressed={active}
                      aria-label={`Filter by ${payer.payer_name}, ${payer.patient_count} ${plural}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1,
                        minHeight: 44,
                        cursor: 'pointer',
                        bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                        borderLeft: active
                          ? `3px solid ${theme.palette.primary.main}`
                          : '3px solid transparent',
                        '&:hover': {
                          bgcolor: active
                            ? alpha(theme.palette.primary.main, 0.12)
                            : theme.palette.action.hover,
                        },
                        transition: 'background-color 0.15s, border-color 0.15s',
                      }}
                    >
                      <Avatar
                        src={payer.avatar_url ?? undefined}
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          bgcolor: active ? theme.palette.primary.main : theme.palette.grey[300],
                          color: active
                            ? theme.palette.primary.contrastText
                            : theme.palette.text.primary,
                        }}
                      >
                        {payer.payer_name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          fontWeight: active ? 600 : 400,
                          color: active ? theme.palette.primary.main : theme.palette.text.primary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {payer.payer_name}
                      </Typography>
                      <Chip
                        label={payer.patient_count}
                        size="small"
                        sx={{
                          height: 20,
                          minWidth: 28,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          bgcolor: active
                            ? alpha(theme.palette.primary.main, 0.15)
                            : theme.palette.grey[100],
                          color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              })}

              {data?.payers.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ px: 2, py: 2, color: theme.palette.text.secondary, textAlign: 'center' }}
                >
                  No payers found
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Coverage */}
        {!isLoading && buckets.length > 0 && (
          <>
            <Divider />
            <Typography
              variant="overline"
              sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary' }}
            >
              Coverage
            </Typography>
            <Box sx={{ py: 0.5 }}>
              {buckets.map(row => (
                <BucketRow
                  key={row.bucket}
                  icon={row.icon}
                  label={row.label}
                  count={row.count}
                  active={isSelected('coverage', row.bucket)}
                  color={row.color}
                  subjectLanguage={plural}
                  onClick={() => onToggle('coverage', row.bucket)}
                />
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* Requirements render nothing unless the org is behind the gate, the
          user can manage billing, and the catalog holds an active definition.
          Rows stay enabled during a search: search and filters compose. */}
      <RequirementsRailCard
        activeDefinitionIds={selected.missing}
        onSelect={onRequirementSelect}
        onDeselect={onRequirementDeselect}
        showOnSmallScreens={inMenu}
      />
    </>
  );
};

/** The desktop rail: the quick filters plus cards other features contribute. */
export const PayerStatsSidebar: React.FC<QuickFiltersProps> = props => (
  <Box
    component="aside"
    aria-label="Quick filters"
    sx={{
      width: 240,
      flexShrink: 0,
      display: { xs: 'none', md: 'block' },
    }}
  >
    <Box sx={{ position: 'sticky', top: 24 }}>
      <QuickFilters {...props} />
      <EligibilityChecksSummaryCard />
    </Box>
  </Box>
);

// ── Internal sub-component ─────────────────────────────────────

interface BucketRowProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  active: boolean;
  color: string;
  subjectLanguage: string;
  onClick: () => void;
}

const BucketRow: React.FC<BucketRowProps> = ({
  icon,
  label,
  count,
  active,
  color,
  subjectLanguage,
  onClick,
}) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-pressed={active}
      aria-label={`Filter by ${label}, ${count} ${subjectLanguage}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        minHeight: 44,
        cursor: 'pointer',
        bgcolor: active ? alpha(color, 0.08) : 'transparent',
        borderLeft: active ? `3px solid ${color}` : '3px solid transparent',
        '&:hover': {
          bgcolor: active ? alpha(color, 0.12) : theme.palette.action.hover,
        },
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      {icon}
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          fontWeight: active ? 600 : 400,
          color: active ? color : theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>
      <Chip
        label={count}
        size="small"
        sx={{
          height: 20,
          minWidth: 28,
          fontSize: '0.7rem',
          fontWeight: 600,
          bgcolor: active ? alpha(color, 0.15) : theme.palette.grey[100],
          color: active ? color : theme.palette.text.secondary,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    </Box>
  );
};
